import React, { ChangeEvent } from 'react';
import { useMessage } from '@tmapy/intl';
import { useDispatch, useSelector } from '@tmapy/redux';

import {
  BtnBar,
  BtnBarSpan,
  Checkbox,
  CtaBtn,
  FormItem,
  FormRow,
  SecondaryBtn,
  StickyFooter,
  SvgArrowLeft,
  SvgArrowRight,
  Textarea,
} from '@tmapy/style-guide';

import type { InputProps } from '../../hooks/useInput';
import { useAuth, useFormItemServerErrors, useInput } from '../../hooks';
import { OriginEnum } from '../../openapi/types/OriginEnum';
import { ServiceOrderNoteField } from '../../serviceOrders/components/ServiceOrderNoteField';
import { canSetCounterDetails } from '../utils/canSetCounterDetails';
import { newOrderMessages } from './orderMessages';
import {
  actionSetHandlingFee,
  actionSetNote,
  actionSetOrigin,
  actionSetReason,
} from '../actions/newOrder';
import { NewOrderAttachments } from './NewOrderAttachments';
import { FormattedMessage } from 'react-intl';

type ReasonProps = {
  onPrevStepClick(): void;
  onNextStepClick(): void;
};

export const Reason: React.FC<ReasonProps> = ({ onPrevStepClick, onNextStepClick }) => {
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const auth = useAuth();
  const customerType = useSelector((state) => state.app.config.customerType);
  const initialReason = useSelector((state) => state.newOrder.reason);
  const {
    id: orderId,
    handling_fee,
    note,
    origin,
    fieldErrors,
  } = useSelector((state) => state.newOrder);
  const formItemServerErrors = useFormItemServerErrors(fieldErrors);

  // IPR místo důvodu žádosti zapisuje poznámku, která se tiskne na dokladech.
  const isReasonHidden = customerType === 'ipr';
  const isCounterSectionVisible = canSetCounterDetails(customerType, auth.hasPermission);
  // Úprava objednávky původ, poznámku ani poplatek na backendu nemění.
  const areCounterDetailsLocked = !!orderId;

  const reason = useInput<InputProps>({
    name: 'reason',
    initialValue: initialReason,
    onChange: (e: ChangeEvent) => dispatch(actionSetReason((e.target as any)?.value)),
  });

  const handleNoteChange = (value: string) => dispatch(actionSetNote(value));

  const handleHandlingFeeChange = (e: React.ChangeEvent) =>
    dispatch(actionSetHandlingFee((e.target as HTMLInputElement).checked));

  const handleOriginChange = (e: React.ChangeEvent) =>
    dispatch(
      actionSetOrigin(
        (e.target as HTMLInputElement).checked ? OriginEnum.COUNTER : OriginEnum.EVYDEJ,
      ),
    );

  return (
    <>
      {!isReasonHidden && (
        <div className='sg-u-vs-2'>
          <Textarea {...reason} placeholder={formatMessage(newOrderMessages.reasonPlaceholder)} />
        </div>
      )}
      {isCounterSectionVisible && (
        <section>
          <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
            <FormattedMessage {...newOrderMessages.counterDetailsTitle} />
          </h3>
          <div className='sg-u-box sg-a-p-2 sg-u-vs-2'>
            <FormRow>
              <FormItem label={formatMessage(newOrderMessages.originLabel)}>
                <Checkbox
                  name='origin'
                  isChecked={origin === OriginEnum.COUNTER}
                  isDisabled={areCounterDetailsLocked}
                  onChange={handleOriginChange}
                >
                  <FormattedMessage {...newOrderMessages.origin} />
                </Checkbox>
              </FormItem>
            </FormRow>
            <FormRow>
              <ServiceOrderNoteField
                value={note}
                isDisabled={areCounterDetailsLocked}
                serverError={formItemServerErrors.note}
                onChange={handleNoteChange}
              />
            </FormRow>
            <FormRow>
              <FormItem label={formatMessage(newOrderMessages.handlingFeeLabel)}>
                <Checkbox
                  name='handling_fee'
                  isChecked={handling_fee}
                  isDisabled={areCounterDetailsLocked}
                  onChange={handleHandlingFeeChange}
                >
                  <FormattedMessage {...newOrderMessages.handlingFee} />
                </Checkbox>
              </FormItem>
            </FormRow>
            {areCounterDetailsLocked && (
              <div className='sg-u-type-sml sg-u-c-body-darkest'>
                <FormattedMessage {...newOrderMessages.counterDetailsLockedHint} />
              </div>
            )}
          </div>
        </section>
      )}
      <div className='sg-a-mt-4'>
        <NewOrderAttachments />
      </div>
      <StickyFooter>
        <BtnBar>
          <CtaBtn
            icon={{ position: 'right', element: <SvgArrowRight /> }}
            onClick={onNextStepClick}
          >
            <FormattedMessage {...newOrderMessages.summaryNextBtnLabel} />
          </CtaBtn>
          <BtnBarSpan />
          <SecondaryBtn
            icon={{ position: 'left', element: <SvgArrowLeft /> }}
            onClick={onPrevStepClick}
          >
            <FormattedMessage {...newOrderMessages.selectRegionPrevBtnLabel} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </>
  );
};
